import { useState, useRef } from "react";
import { buildTasks, getAgentFees, genId, STAGES, emptyParty } from "./aks_transactions";
import { uploadImportStagingFile, analyzeTransactionDocuments } from "./supabaseApi";

// Paid add-on: drop a batch of transaction documents, an LLM extracts the
// transaction details and guesses which checklist item each file matches,
// then the user reviews/edits everything before anything is created. Nothing
// is written to the database until "Create Transaction" — and even then,
// uploading a matched file never marks its checklist item "completed"; that
// stays a manual human action, same as the rest of the app.
const B = {
  red:"#A51C30", black:"#141414", charcoal:"#222222", gray:"#4A4A4A",
  silver:"#8A8A8A", light:"#E8E8E8", offWhite:"#F5F4F2", white:"#FFFFFF",
  green:"#16A34A", teal:"#0891B2",
};

const MAX_TOTAL_BYTES = 20 * 1024 * 1024;
const MAX_FILES = 15;
const CONFIDENCE_THRESHOLD = 0.6;

const inputSt = { width:"100%", padding:"9px 12px", borderRadius:8, border:"1px solid "+B.light, fontSize:13, color:B.black, fontFamily:"'DM Sans',sans-serif", background:B.offWhite, outline:"none", boxSizing:"border-box" };
const labelSt = { fontSize:10, fontWeight:700, color:B.silver, letterSpacing:"0.09em", textTransform:"uppercase", marginBottom:4, display:"block", fontFamily:"'DM Sans',sans-serif" };

function fmtBytes(n) {
  if (n < 1024*1024) return Math.round(n/1024) + " KB";
  return (n/1024/1024).toFixed(1) + " MB";
}

export default function DocumentImportModal({ currentUser, users, checklistTemplates, transactions, brokerage, dbOps, onClose, onImported, onQuotaUpdate }) {
  const [phase, setPhase] = useState("drop"); // drop | analyzing | review | uploading | done
  const [files, setFiles] = useState([]);
  const [error, setError] = useState("");
  const [form, setForm] = useState(null);
  const [parties, setParties] = useState([]);
  const [fileAssignments, setFileAssignments] = useState([]); // [{file, templateId, confidence}]
  const [uploadResults, setUploadResults] = useState([]);
  const [createdTx, setCreatedTx] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef();
  const importIdRef = useRef(genId());

  const eligibleAgents = currentUser.role === "realtor"
    ? users.filter(u => u.id === currentUser.id)
    : currentUser.role === "associate_broker"
    ? users.filter(u => u.id === currentUser.id || (u.role === "realtor" && u.brokerId === currentUser.id))
    : users;

  const atLimit = brokerage?.maxTransactions != null && (transactions?.length || 0) >= brokerage.maxTransactions;
  const importsRemaining = Math.max(0, (brokerage?.documentImportQuota || 0) - (brokerage?.documentImportUsed || 0));
  const noImportsLeft = importsRemaining <= 0;

  function addFiles(fileList) {
    setError("");
    const incoming = Array.from(fileList);
    const all = [...files, ...incoming];
    if (all.length > MAX_FILES) { setError(`Too many files — max ${MAX_FILES} per import.`); return; }
    const totalBytes = all.reduce((s, f) => s + f.size, 0);
    if (totalBytes > MAX_TOTAL_BYTES) {
      setError(`Combined size exceeds ${Math.round(MAX_TOTAL_BYTES / 1024 / 1024)}MB — try splitting into two imports.`);
      return;
    }
    setFiles(all);
  }
  function removeFile(idx) { setFiles(files.filter((_, i) => i !== idx)); }

  async function startAnalysis() {
    if (!files.length) { setError("Drop at least one file."); return; }
    setPhase("analyzing");
    setError("");
    try {
      const importId = importIdRef.current;
      const stagingPaths = [];
      for (const f of files) {
        stagingPaths.push(await uploadImportStagingFile(currentUser.id, importId, f));
      }
      const result = await analyzeTransactionDocuments({
        stagingPaths,
        filenames: files.map(f => f.name),
        checklistTemplates: checklistTemplates.map(t => ({
          templateId: t.templateId, title: t.title, category: t.category, description: t.description, side: t.side,
        })),
      });
      try { sessionStorage.setItem("aks_import_analysis", JSON.stringify(result)); } catch {}

      // Update the parent's brokerage state right away so the "N remaining"
      // caption elsewhere in the app doesn't stay stale until a full refetch.
      if (onQuotaUpdate && result._documentImportUsed != null) {
        onQuotaUpdate({ documentImportUsed: result._documentImportUsed, documentImportQuota: result._documentImportQuota });
      }

      const agentId = currentUser.id;
      const fees = getAgentFees(agentId, users);
      const side = ["Buyer", "Seller"].includes(result.transaction?.side) ? result.transaction.side : "Buyer";
      setForm({
        agentId, side,
        address: result.transaction?.address || "",
        price: result.transaction?.price || "",
        stage: "Under Contract", commissionPct: 2.5, referralFee: 0,
        agentSplit: fees.agentSplit, transactionFee: fees.transactionFee, eoFee: fees.eoFee, customFees: fees.customFees || [],
        contractDate: result.transaction?.contractDate || "",
        inspectionDeadline: result.transaction?.inspectionDeadline || "",
        appraisalDeadline: result.transaction?.appraisalDeadline || "",
        closingDate: result.transaction?.closingDate || "",
        notes: result.transaction?.notes || "",
      });
      const names = (result.transaction?.clientNames || []).filter(Boolean);
      setParties(names.map(n => ({ ...emptyParty(side === "Buyer" ? "buyer" : "seller"), name: n })));

      const validTemplateIds = new Set(buildTasks(side, checklistTemplates).map(t => t.templateId));
      setFileAssignments(files.map((f, idx) => {
        const match = (result.fileMatches || []).find(m => m.filename === f.name || m.fileIndex === idx);
        const templateId = match?.matchedTemplateId;
        const isValid = templateId && validTemplateIds.has(templateId) && (match.confidence || 0) >= CONFIDENCE_THRESHOLD;
        return { file: f, templateId: isValid ? templateId : "", confidence: match?.confidence || 0 };
      }));

      setPhase("review");
    } catch (e) {
      setError(e.message || "Analysis failed. Please try again.");
      setPhase("drop");
    }
  }

  function handleSideChange(newSide) {
    setForm(f => ({ ...f, side: newSide }));
    setParties(ps => ps.map(p => ({ ...p, role: newSide === "Buyer" ? "buyer" : "seller" })));
    const validTemplateIds = new Set(buildTasks(newSide, checklistTemplates).map(t => t.templateId));
    setFileAssignments(prev => prev.map(a => (validTemplateIds.has(a.templateId) ? a : { ...a, templateId: "" })));
  }

  function handleAgentChange(newAgentId) {
    const fees = getAgentFees(newAgentId, users);
    setForm(f => ({ ...f, agentId: newAgentId, agentSplit: fees.agentSplit, transactionFee: fees.transactionFee, eoFee: fees.eoFee, customFees: fees.customFees || [] }));
  }

  const validTasksForSide = form ? buildTasks(form.side, checklistTemplates) : [];

  async function handleCreate() {
    setPhase("uploading");
    setError("");
    try {
      const derivedClientName = parties.map(p => p.name).filter(Boolean).join(" & ");
      const tx = {
        ...form, id: genId(),
        price: Number(form.price) || 0, commissionPct: Number(form.commissionPct),
        referralFee: Number(form.referralFee || 0), agentSplit: Number(form.agentSplit),
        transactionFee: Number(form.transactionFee || 0), eoFee: Number(form.eoFee || 0),
        customFees: form.customFees || [], clientName: derivedClientName, parties, contacts: [],
        tasks: buildTasks(form.side, checklistTemplates), wfState: "draft", reviewLog: [], submittedAt: null,
      };
      const created = await dbOps.createTx(tx);
      setCreatedTx(created);

      const templateIdToTaskId = Object.fromEntries((created.tasks || []).map(t => [t.templateId, t.id]));
      const results = [];
      for (const a of fileAssignments) {
        const taskId = a.templateId ? templateIdToTaskId[a.templateId] : null;
        if (!taskId) { results.push({ file: a.file, status: "skipped" }); continue; }
        try {
          await dbOps.uploadDoc(taskId, created.id, a.file);
          results.push({ file: a.file, templateId: a.templateId, taskId, status: "success" });
        } catch (e) {
          results.push({ file: a.file, templateId: a.templateId, taskId, status: "error", message: e.message });
        }
      }
      setUploadResults(results);
      try { sessionStorage.removeItem("aks_import_analysis"); } catch {}
      setPhase("done");
    } catch (e) {
      setError(e.message || "Could not create the transaction. Please try again — nothing was lost.");
      setPhase("review");
    }
  }

  async function retryUpload(idx) {
    const r = uploadResults[idx];
    if (!createdTx || !r.taskId) return;
    setUploadResults(prev => prev.map((x, i) => (i === idx ? { ...x, status: "retrying" } : x)));
    try {
      await dbOps.uploadDoc(r.taskId, createdTx.id, r.file);
      setUploadResults(prev => prev.map((x, i) => (i === idx ? { ...x, status: "success" } : x)));
    } catch (e) {
      setUploadResults(prev => prev.map((x, i) => (i === idx ? { ...x, status: "error", message: e.message } : x)));
    }
  }

  function finish() {
    onClose();
    if (createdTx) onImported(createdTx);
  }

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
      <div style={{ background:B.white, borderRadius:16, width:"100%", maxWidth:720, maxHeight:"92vh", overflow:"auto", display:"flex", flexDirection:"column", boxShadow:"0 20px 60px rgba(0,0,0,0.25)" }}>

        <div style={{ padding:"20px 28px", borderBottom:"1px solid "+B.light, flexShrink:0, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <h2 style={{ margin:0, fontSize:20, fontFamily:"'Playfair Display',serif", color:B.black, display:"flex", alignItems:"center", gap:10 }}>
            <span style={{width:32,height:32,borderRadius:"50%",background:B.white,border:"1px solid "+B.light,display:"flex",alignItems:"center",justifyContent:"center",fontSize:17,flexShrink:0,boxShadow:"0 1px 3px rgba(0,0,0,0.15)"}}>🧞</span>
            Transaction Genie
          </h2>
          {phase !== "uploading" && (
            <button onClick={onClose} style={{ background:"none", border:"none", fontSize:22, cursor:"pointer", color:B.gray }}>✕</button>
          )}
        </div>

        <div style={{ padding:"22px 28px" }}>

          {noImportsLeft && phase === "drop" && (
            <div style={{ padding:"14px 16px", borderRadius:10, background:"#FEF2F2", border:"1px solid #FCA5A5", color:"#991B1B", fontSize:13, fontFamily:"'DM Sans',sans-serif" }}>
              No imports remaining ({brokerage?.documentImportUsed || 0} of {brokerage?.documentImportQuota || 0} used). Purchase another bundle of 10 to keep importing.
            </div>
          )}

          {!noImportsLeft && atLimit && phase === "drop" && (
            <div style={{ padding:"14px 16px", borderRadius:10, background:"#FEF2F2", border:"1px solid #FCA5A5", color:"#991B1B", fontSize:13, fontFamily:"'DM Sans',sans-serif" }}>
              This brokerage has reached its transaction limit ({brokerage.maxTransactions}). Contact support to increase it before importing more.
            </div>
          )}

          {!noImportsLeft && !atLimit && phase === "drop" && (
            <>
              <p style={{ margin:"0 0 16px", fontSize:13, color:B.gray, fontFamily:"'DM Sans',sans-serif" }}>
                Drop the contract, disclosures, and other documents for a deal — AI will extract the transaction details and match each file to a checklist item for you to review before anything is created.
              </p>
              <div
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={e => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
                onClick={() => fileInputRef.current?.click()}
                style={{ border:`2px dashed ${dragOver ? B.red : B.light}`, borderRadius:12, padding:"40px 20px", textAlign:"center", cursor:"pointer", background:dragOver ? "#FFF5F5" : B.offWhite }}>
                <div style={{ fontSize:32, marginBottom:8 }}>📄</div>
                <div style={{ fontSize:13, fontWeight:700, color:B.black, fontFamily:"'DM Sans',sans-serif" }}>Drop files here, or click to browse</div>
                <div style={{ fontSize:11, color:B.silver, marginTop:4, fontFamily:"'DM Sans',sans-serif" }}>PDF or image files, up to {MAX_FILES} files / {Math.round(MAX_TOTAL_BYTES/1024/1024)}MB total</div>
                <input ref={fileInputRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.webp" style={{ display:"none" }}
                  onChange={e => { if (e.target.files.length) addFiles(e.target.files); e.target.value = ""; }} />
              </div>

              {files.length > 0 && (
                <div style={{ marginTop:16, display:"flex", flexDirection:"column", gap:8 }}>
                  {files.map((f, i) => (
                    <div key={i} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"8px 12px", borderRadius:8, border:"1px solid "+B.light, background:B.white }}>
                      <div style={{ fontSize:12.5, color:B.black, fontFamily:"'DM Sans',sans-serif" }}>{f.name} <span style={{ color:B.silver }}>· {fmtBytes(f.size)}</span></div>
                      <button onClick={() => removeFile(i)} style={{ background:"none", border:"none", color:B.silver, cursor:"pointer", fontSize:14 }}>✕</button>
                    </div>
                  ))}
                </div>
              )}

              {error && <div style={{ marginTop:12, fontSize:12.5, color:"#DC2626", fontFamily:"'DM Sans',sans-serif" }}>{error}</div>}

              <div style={{ marginTop:20, display:"flex", justifyContent:"flex-end", alignItems:"center", gap:12 }}>
                {files.length > 0 && (
                  <span style={{ fontSize:11, color:B.silver, fontFamily:"'DM Sans',sans-serif" }}>
                    Uses 1 of your {importsRemaining} remaining import{importsRemaining !== 1 ? "s" : ""}
                  </span>
                )}
                <button onClick={onClose} style={{ padding:"9px 18px", borderRadius:8, border:"1px solid "+B.light, background:B.white, color:B.gray, cursor:"pointer", fontSize:13, fontWeight:700, fontFamily:"'DM Sans',sans-serif" }}>Cancel</button>
                <button onClick={startAnalysis} disabled={!files.length}
                  style={{ padding:"9px 20px", borderRadius:8, border:"none", background:files.length ? B.red : B.light, color:files.length ? B.white : B.silver, cursor:files.length ? "pointer" : "not-allowed", fontSize:13, fontWeight:700, fontFamily:"'DM Sans',sans-serif" }}>
                  Analyze {files.length > 0 ? `${files.length} File${files.length !== 1 ? "s" : ""}` : ""}
                </button>
              </div>
            </>
          )}

          {phase === "analyzing" && (
            <div style={{ padding:"50px 0", textAlign:"center" }}>
              <div style={{width:40,height:40,margin:"0 auto 16px",border:"3px solid "+B.light,borderTop:"3px solid "+B.red,borderRadius:"50%",animation:"spin 0.8s linear infinite"}}/>
              <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
              <div style={{ fontSize:13, color:B.gray, fontFamily:"'DM Sans',sans-serif" }}>Reading your documents…</div>
            </div>
          )}

          {phase === "review" && form && (
            <>
              <div style={{ padding:"12px 14px", borderRadius:10, background:"#FFFBEB", border:"1px solid #FDE68A", marginBottom:16, display:"flex", gap:10, alignItems:"flex-start" }}>
                <span style={{ fontSize:16, lineHeight:1 }}>⚠️</span>
                <span style={{ fontSize:12.5, color:"#92400E", fontFamily:"'DM Sans',sans-serif" }}>
                  <strong>AI-extracted data may not always be accurate.</strong> Nothing is saved yet — carefully check every field and file match below against the actual documents before creating the transaction.
                </span>
              </div>

              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:14 }}>
                <div>
                  <label style={labelSt}>Agent</label>
                  <select value={form.agentId} onChange={e => handleAgentChange(e.target.value)} style={inputSt}>
                    {eligibleAgents.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelSt}>Side</label>
                  <select value={form.side} onChange={e => handleSideChange(e.target.value)} style={inputSt}>
                    <option value="Buyer">Buyer</option>
                    <option value="Seller">Seller</option>
                  </select>
                </div>
              </div>

              <div style={{ marginBottom:14 }}>
                <label style={labelSt}>Property Address</label>
                <input value={form.address} onChange={e => setForm(f => ({ ...f, address:e.target.value }))} style={inputSt} />
              </div>

              <div style={{ marginBottom:14 }}>
                <label style={labelSt}>Client Name(s)</label>
                <input value={parties.map(p=>p.name).join(", ")}
                  onChange={e => setParties(e.target.value.split(",").map(s=>s.trim()).filter(Boolean).map(n => ({ ...emptyParty(form.side==="Buyer"?"buyer":"seller"), name:n })))}
                  placeholder="Comma-separated" style={inputSt} />
              </div>

              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:14 }}>
                <div>
                  <label style={labelSt}>Sale Price ($)</label>
                  <input type="number" value={form.price} onChange={e => setForm(f => ({ ...f, price:e.target.value }))} style={inputSt} />
                </div>
                <div>
                  <label style={labelSt}>Stage</label>
                  <select value={form.stage} onChange={e => setForm(f => ({ ...f, stage:e.target.value }))} style={inputSt}>
                    {STAGES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>

              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:14 }}>
                <div>
                  <label style={labelSt}>Contract Date</label>
                  <input type="date" value={form.contractDate} onChange={e => setForm(f => ({ ...f, contractDate:e.target.value }))} style={inputSt} />
                </div>
                <div>
                  <label style={labelSt}>Closing Date</label>
                  <input type="date" value={form.closingDate} onChange={e => setForm(f => ({ ...f, closingDate:e.target.value }))} style={inputSt} />
                </div>
              </div>

              <div style={{ marginBottom:14 }}>
                <label style={labelSt}>Notes <span style={{ fontWeight:400, color:B.silver }}>(from the contract document — review before confirming)</span></label>
                <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes:e.target.value }))} rows={3}
                  style={{ ...inputSt, resize:"vertical" }} />
              </div>

              <div style={{ display:"flex", alignItems:"center", gap:10, margin:"18px 0 12px" }}>
                <div style={{ height:1, flex:1, background:B.light }} />
                <span style={{ fontSize:10, fontWeight:700, color:B.silver, letterSpacing:"0.1em", textTransform:"uppercase", fontFamily:"'DM Sans',sans-serif" }}>Files &amp; Checklist Matches</span>
                <div style={{ height:1, flex:1, background:B.light }} />
              </div>

              <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                {fileAssignments.map((a, i) => (
                  <div key={i} style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 12px", borderRadius:8, border:`1px solid ${a.templateId ? B.light : "#FCA5A5"}`, background:a.templateId ? B.white : "#FFF7F7" }}>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:12.5, color:B.black, fontFamily:"'DM Sans',sans-serif", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{a.file.name}</div>
                      {!a.templateId && <div style={{ fontSize:10.5, color:"#DC2626", fontWeight:700, fontFamily:"'DM Sans',sans-serif", marginTop:2 }}>⚠ Unmatched — please assign</div>}
                    </div>
                    <select value={a.templateId} onChange={e => setFileAssignments(prev => prev.map((x,idx)=>idx===i?{...x,templateId:e.target.value}:x))}
                      style={{ ...inputSt, width:220 }}>
                      <option value="">— Not attached —</option>
                      {validTasksForSide.map(t => <option key={t.templateId} value={t.templateId}>{t.title}</option>)}
                    </select>
                  </div>
                ))}
              </div>

              {error && <div style={{ marginTop:14, fontSize:12.5, color:"#DC2626", fontFamily:"'DM Sans',sans-serif" }}>{error}</div>}

              <div style={{ marginTop:20, display:"flex", justifyContent:"flex-end", gap:10 }}>
                <button onClick={onClose} style={{ padding:"9px 18px", borderRadius:8, border:"1px solid "+B.light, background:B.white, color:B.gray, cursor:"pointer", fontSize:13, fontWeight:700, fontFamily:"'DM Sans',sans-serif" }}>Cancel</button>
                <button onClick={handleCreate} style={{ padding:"9px 20px", borderRadius:8, border:"none", background:B.red, color:B.white, cursor:"pointer", fontSize:13, fontWeight:700, fontFamily:"'DM Sans',sans-serif" }}>Create Transaction</button>
              </div>
            </>
          )}

          {phase === "uploading" && (
            <div style={{ padding:"50px 0", textAlign:"center" }}>
              <div style={{width:40,height:40,margin:"0 auto 16px",border:"3px solid "+B.light,borderTop:"3px solid "+B.red,borderRadius:"50%",animation:"spin 0.8s linear infinite"}}/>
              <div style={{ fontSize:13, color:B.gray, fontFamily:"'DM Sans',sans-serif" }}>Creating transaction and uploading documents…</div>
            </div>
          )}

          {phase === "done" && (
            <>
              <div style={{ padding:"12px 16px", borderRadius:10, background:"#F0FDF4", border:"1px solid #BBF7D0", color:"#15803D", fontSize:13, fontFamily:"'DM Sans',sans-serif", marginBottom:16 }}>
                ✓ Transaction created. Remember to verify each document and mark checklist items complete yourself — nothing was auto-completed.
              </div>
              <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                {uploadResults.map((r, i) => (
                  <div key={i} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"8px 12px", borderRadius:8, border:"1px solid "+B.light }}>
                    <div style={{ fontSize:12.5, color:B.black, fontFamily:"'DM Sans',sans-serif" }}>{r.file.name}</div>
                    {r.status === "success" && <span style={{ fontSize:11, fontWeight:700, color:B.green, fontFamily:"'DM Sans',sans-serif" }}>✓ Uploaded</span>}
                    {r.status === "skipped" && <span style={{ fontSize:11, color:B.silver, fontFamily:"'DM Sans',sans-serif" }}>Not attached</span>}
                    {r.status === "retrying" && <span style={{ fontSize:11, color:B.silver, fontFamily:"'DM Sans',sans-serif" }}>Retrying…</span>}
                    {r.status === "error" && (
                      <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                        <span style={{ fontSize:11, fontWeight:700, color:"#DC2626", fontFamily:"'DM Sans',sans-serif" }}>Failed</span>
                        <button onClick={() => retryUpload(i)} style={{ padding:"4px 10px", borderRadius:6, border:"none", background:B.teal, color:B.white, cursor:"pointer", fontSize:11, fontWeight:700, fontFamily:"'DM Sans',sans-serif" }}>Retry</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div style={{ marginTop:20, display:"flex", justifyContent:"flex-end" }}>
                <button onClick={finish} style={{ padding:"9px 20px", borderRadius:8, border:"none", background:B.red, color:B.white, cursor:"pointer", fontSize:13, fontWeight:700, fontFamily:"'DM Sans',sans-serif" }}>Go to Checklist →</button>
              </div>
            </>
          )}

        </div>
      </div>
    </div>
  );
}
